/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const NatCal = require('../types/NatCal')
const CAffichageLieAPoint = require('./CAffichageLieAPoint')
const CLatex = require('./CLatex')
module.exports = CValeurAffichee

/**
 * Classe représentant une valeur affichée sur la figure.
 * @constructor
 * @extends CAffichageLieAPoint
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {Color} couleur : La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {float} x_Nom : L'abscisse d'affichage de l'éditeur
 * @param {float} y_Nom : L'ordonnée d'affichage de l'éditeur
 * @param {int} dec_x : Décalage horizontal du nom
 * @param {int} dec_y : Décalage vertical du nom
 * @param {boolean} masque : true si l'éditeur est masqué
 * @param {CPt} pointLie : null ou pointe sur un point auquel l'affichage est lié.
 * @param {int} taillePolice : Indice de la taiile de police utilisée
 * @param {boolean} encadrement : true si encadré.
 * @param {boolean} effacementFond : true si on efface le fond de l'en-tête.
 * @param {Color} couleurFond : La couleur de fond de l'en-tête.
 * @param {int} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {int} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {CValDyn} valeurAssociee : La valeur à afficher
 * @param {string} enTete : le texte qui doit précéder l'affichage de valeur.
 * Encadrer de deux $ pour avoir un affichage LaTeX.
 * @param {string} postChaine : Le texet qui doit suivre l'affichage de la valeur.
 * @param {int} nombreDecimales : Le nombre de décimales du résultat.
 * @constructor
 */
function CValeurAffichee (listeProprietaire, impProto, estElementFinal, couleur, x_Nom, y_Nom,
    dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond, couleurFond, alignementHorizontal,
    alignementVertical, valeurAssociee, enTete, postChaine, nombreDecimales) {
  if (arguments.length === 1) CAffichageLieAPoint.call(this, listeProprietaire);
  else {
    CAffichageLieAPoint.call(this, listeProprietaire, impProto, estElementFinal, couleur, x_Nom, y_Nom,
      dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond, couleurFond, alignementHorizontal,
        alignementVertical);
    this.valeurAssociee = valeurAssociee;
    this.enTete = enTete;
    this.postChaine = postChaine;
    this.nombreDecimales = nombreDecimales;
  }
}
CValeurAffichee.prototype = new CAffichageLieAPoint();
CValeurAffichee.prototype.constructor = CValeurAffichee;
CValeurAffichee.prototype.superClass = "CAffichageLieAPoint";
CValeurAffichee.prototype.className = "CValeurAffichee";


CValeurAffichee.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.valeurAssociee);
  var ind2 = listeSource.indexOf(this.pointLie);
  var ind3 = listeSource.indexOf(this.impProto);
  return new CValeurAffichee(listeCible, listeCible.get(ind3, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.x_nom, this.y_nom, this.dec_x, this.dec_y, this.masque,
    listeCible.get(ind2, "CPt"), this.taillePolice, this.encadrement,
    this.effacementFond, this.couleurFond, this.alignementHorizontal, this.alignementVertical,
    listeCible.get(ind1, "CValDyn"), this.enTete, this.postChaine, this.nombreDecimales);
};

CValeurAffichee.prototype.getNature = function() {
  return NatObj.NValeurAffichee;
};

CValeurAffichee.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CAffichageLieAPoint.prototype.dependDe.call(this, p) || this.valeurAssociee.dependDe(p));
};

CValeurAffichee.prototype.dependDePourBoucle = function(p) {
  return CAffichageLieAPoint.prototype.dependDePourBoucle.call(this, p) || this.valeurAssociee.dependDePourBoucle(p);
};

CValeurAffichee.prototype.positionne = function(infoRandom, dimf) {
  CAffichageLieAPoint.prototype.positionne.call(this, infoRandom, dimf);
  this.existe = this.existe && this.valeurAssociee.existe;
};

CValeurAffichee.prototype.rendChaineAffichage = function() {
    if (this.valeurAssociee.estDeNatureCalcul(NatCal.NTteValC)) {
    return this.enTete + this.valeurAssociee.rendChaineValeurComplexe(this.nombreDecimales) + this.postChaine;
  }
  else {
    return this.enTete + this.valeurAssociee.rendChaineValeur(this.nombreDecimales) + this.postChaine;
  }
};

CValeurAffichee.prototype.chaineDesignation = function() {
  return "desAffichageValeur";
};

CValeurAffichee.prototype.read = function(inps, list){
  CAffichageLieAPoint.prototype.read.call(this, inps, list);
  this.enTete = CLatex.traiteAccents(inps.readUTF());
  this.postChaine = CLatex.traiteAccents(inps.readUTF());
  this.nombreDecimales = inps.readByte();
  var ind1 = inps.readInt();
  this.valeurAssociee = list.get(ind1,"CValDyn");
};

CValeurAffichee.prototype.write = function(oups, list){
  CAffichageLieAPoint.prototype.write.call(this, oups, list);
  oups.writeUTF(this.enTete);
  oups.writeUTF(this.postChaine);
  oups.writeByte(this.nombreDecimales);
  var ind1 = list.indexOf(this.valeurAssociee);
  oups.writeInt(ind1);
};



// WEBPACK FOOTER //
// ./src/objets/CValeurAffichee.js